import moment from 'moment'
import EventBus from '@/components/mixins/event-bus-mixin'
import MomentTimezone from 'moment-timezone'
import _ from 'lodash'
import { proxyException } from '@/utils/communication'
import { APP } from '@base-shared/utils/constants';

/**
 * Axios instance dependency
 * @type {import('axios').AxiosInstance}
 */

/**
 * Mini dependency manager
 */
export const Dependencies = (() => {
  let repository = window?.dependenciesRegistry ?? {}
  window.dependenciesRegistry = repository

  return {
    provide: (key, dependency) => {
      if (repository[key]) {
        throw new Error('Attempt to register same dependency twice: ' + key)
      }
      repository[key] = dependency
    },
    inject: (key) => {
      if (!repository[key]) {
        throw new Error('No dependency registered for the key: ' + key)
      }
      return repository[key]
    },
    reset: () => {
      repository = {}
      window.dependenciesRegistry = repository
    }
  }
})()

export default {
  options: {
    mediaFileType: Object.freeze({
      img: 'img',
      video: 'video',
      project: 'project',
      scheduled: 'scheduled',
      audio: 'audio',
      elementi: 'elementi',
      audioPlaylist: 'audio-playlist'
    }),
    plans: Object.freeze({
      DISCOVERY: 'discovery',
      PREMIUM: 'premium'
    }),
    mediaDuration: Object.freeze({
      DEFAULT_MEDIA_SEC: 10,
      DEFAULT_ALERT_SEC: 60
    })
  },

  /**
   * Getter for api instance dependency
   * @returns {import('axios').AxiosInstance}
   */
  getApiInstance () {
    const $api = Dependencies.inject('$api');
    if (!$api) {
      throw new Error('API Instance was not instantiated')
    }

    return $api
  },

  async apiRequest (request, defaultMessage, showLoader) {
    if (showLoader) {
      this.mainLoader(true)
    }
    const { ok: response, error } = await proxyException(request)

    this.mainLoader(false)

    if (error) {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: error.response ? error.response?.data.message ?? error.message : defaultMessage
        }
      })

      return null
    }

    return response.data
  },
  /**
   * This function adds or removes a listener to monitor changes in page or tab visibility within the browser
   * @param {Function} hiddenAction - The action to execute when the page/tab is hidden
   * @param {Function} visibleAction - The action to execute when the page/tab is visible
   */
  handleDocumentVisibilityChange (hiddenAction, visibleAction) {
    const visibilityProps = [
      { hidden: 'hidden', event: 'visibilitychange' },
      { hidden: 'msHidden', event: 'msvisibilitychange' },
      { hidden: 'webkitHidden', event: 'webkitvisibilitychange' }
    ]
    const prop = _.find(visibilityProps, prop => !_.isUndefined(document[prop.hidden]))

    if (_.isEmpty(prop) || !_.isObject(prop)) {
      return
    }

    const visibilityChangeCallback = () => {
      if (document[prop.hidden] && _.isFunction(hiddenAction)) {
        hiddenAction()
      } else if (!document[prop.hidden] && _.isFunction(visibleAction)) {
        visibleAction()
      }
    }

    document.addEventListener(prop.event, visibilityChangeCallback)
    return () => document.removeEventListener(prop.event, visibilityChangeCallback)
  },
  animate (element, classes) {
    return $(element).addClass(classes)
  },
  /**
   * This will handle object clone
   * @param {Object} objectSource - Object to be copied
   */
  copyObject (objectSource) {
    return JSON.parse(JSON.stringify(objectSource))
  },
  clone (dataSrc) {
    return _.cloneDeep(dataSrc)
  },
  camelcaseToDash (stringSrc) {
    return stringSrc.replace(/[A-Z]/g, m => '-' + m.toLowerCase())
  },
  dashToCamelcase (stringSrc) {
    return _.camelCase(stringSrc)
  },
  decodeBase64Format (data) {
    let res = null

    if (data) {
      const getValue = () => {
        try {
          return JSON.parse(atob(data))
        } catch (err) {
          return null
        }
      }

      res = getValue()
    }

    return res
  },
  encodeBase64Format (dataSrc) {
    let dataToString = ''

    if (this.isObject(dataSrc) || Array.isArray(dataSrc)) {
      dataToString = JSON.stringify(dataSrc)
    } else {
      dataToString = dataSrc.toString()
    }

    return btoa(dataToString)
  },
  generateUniqueID () {
    return Date.now() + Math.random().toString(36).substring(2)
  },
  generateRequestToken () {
    const length = 32
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890'.split('')
    const res = []

    for (let i = 0; i < length; i++) {
      const gen = (Math.random() * (chars.length - 1)).toFixed(0)
      res[i] = chars[gen]
    }

    return res.join('')
  },
  trimDataSrc (urlSrc) {
    let url = urlSrc

    if (urlSrc.includes('token')) {
      url = new URL(urlSrc)
      const searchParams = url.searchParams
      searchParams.delete('token')

      return url
    }

    return url
  },
  getCountryLanguage (ISOCode) {
    if (!ISOCode) return null

    const map = {
      en: 'English',
      fr: 'French',
      nl: 'Dutch',
      de: 'German',
      it: 'Italian',
      se: 'Swedish',
      es: 'Spanish',
      ru: 'Russian',
      pt: 'Portuguese'
    }

    const language = map[ISOCode]

    return `${ISOCode.toUpperCase()}${language ? ` - ${language}` : ''}`
  },
  getPlanNameFromBundledPlan (planNameSrc) {
    switch (planNameSrc) {
      case 'discovery':
        return CONFIG.DISCOVERY_PLAN_NAME
      case 'premium':
        return CONFIG.PREMIUM_PLAN_NAME
      case 'enterprise':
        return CONFIG.ENTERPRISE_PLAN_NAME
      case 'enterprise-demo':
        return `${CONFIG.ENTERPRISE_PLAN_NAME} Demo`
      case 'enterprise-trial':
        return `${CONFIG.ENTERPRISE_PLAN_NAME} Trial`
      case 'enterprise-widgets':
        return `${CONFIG.ENTERPRISE_PLAN_NAME} WIDGETS`
      case 'enterprise-kiosk':
        return `${CONFIG.ENTERPRISE_PLAN_NAME} KIOSK`
      case 'enterprise-systems':
        return `${CONFIG.ENTERPRISE_PLAN_NAME} SYSTEMS`
      default:
        return planNameSrc
    }
  },
  /**
   * This will update the item in the list
   * @param {*} origData - Original data list
   * @param {*} conditionKey - reference key to terget the item
   * @param {*} value - property value
   */
  getItemFromList (origData, conditionKey, value) {
    const data = origData
    for (const key in data) {
      if (data[key][conditionKey] && data[key][conditionKey] === value) {
        return {
          position: key,
          value: data[key]
        }
      }
    }
    return null
  },
  /**
   * This will update the item in the list
   * @param {*} origData - Original data list
   * @param {*} updatedItem - updated item
   * @param {*} conditionKey - reference key to terget the item
   */
  updateItemFromList (origData, updatedItem, conditionKey) {
    const data = origData
    for (const key in data) {
      if (data[key][conditionKey] === updatedItem[conditionKey]) {
        data[key] = updatedItem
      }
    }
    return data
  },
  /**
   * This will delete the item in the list
   * @param {*} origData - Original data list
   * @param {*} itemToDelete - item to delete form the list
   * @param {*} conditionKey - reference key to terget the item
   */
  deleteItemFromList (origData, itemToDelete, conditionKey) {
    const data = origData

    for (const key in data) {
      if (data[key][conditionKey] === itemToDelete[conditionKey]) {
        if (Array.isArray(data)) { // If Array
          data.splice(key, 1)
        } else { // Object
          delete data[key]
        }
        return data
      }
    }

    return data
  },
  /**
   * This will remove duplicate items inside the array
   * @param {Array} arraySrc
   */
  removeDuplicateValuesFromArray (arraySrc) {
    let mostFrequent = 1
    let most = 0
    const item = []

    for (let i = 0; i < arraySrc.length; i++) {
      // This will keep the original item
      if (item.indexOf(arraySrc[i]) < 0) {
        item.push(arraySrc[i])
      }

      // This will check most frequent item and avoid duplicates
      for (let j = i; j < arraySrc.length; j++) {
        if (arraySrc[i] === arraySrc[j]) {
          most++
        }

        if (mostFrequent < most) {
          mostFrequent = most

          if (item.indexOf(arraySrc[i]) < 0) {
            item.push(arraySrc[i])
          }
        }
      }
      most = 0
    }

    return item
  },
  /**
   * This will get most frequent items from array
   * @param {Array} arraySrc
   */
  getMostFrequentItemFromArray (arraySrc) {
    let mostFrequent = 1
    let most = 0
    let item = null

    for (let i = 0; i < arraySrc.length; i++) {
      for (let j = i; j < arraySrc.length; j++) {
        if (arraySrc[i] === arraySrc[j]) {
          most++
        }

        if (mostFrequent < most) {
          mostFrequent = most
          item = arraySrc[i]
        }
      }
      most = 0
    }

    return item
  },
  /**
   * Show or Hide App Main Loader
   * @param {Boolean} visible - main loader visibility
   * @param {String} label - main loader text
   */
  mainLoader (visible, label, hasBackground) {
    const payload = {
      isVisible: visible
    }

    if (!_.isUndefined(label)) {
      payload.label = label
    }

    if (!_.isUndefined(hasBackground)) {
      payload.hasBackground = hasBackground
    }

    EventBus.$emit('MAIN_LOADER_VISIBILE', payload)
  },
  /**
   * Sort array of objects by property value
   * @param {String} property - property name to be sort
   */
  arrayDynamicSort (property) {
    let sortOrder = 1
    if (property[0] === '-') {
      sortOrder = -1
      property = property.substr(1)
    }
    return (a, b) => {
      const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0
      return result * sortOrder
    }
  },
  sortTags ({ tags = [] }) {
    const prefixTag = tags.filter((t) => { return t.value === '_toSeeAllData' }) // Get ALL tag

    let sortedTags = tags.filter((t) => { return t.value !== '_toSeeAllData' }) // Exclude ALL tag

    sortedTags.forEach((tag, indx) => {
      sortedTags[indx].sortValue = tag.value ? tag.value.toLowerCase() : tag.name.toLowerCase()
    })

    sortedTags = _.sortBy(sortedTags, [(t) => {
      return t.sortValue
    }])

    let resTags = prefixTag.concat(sortedTags)

    resTags = _.uniqBy(resTags, 'value') // Remove duplicate tags

    return resTags
  },
  getBrowser () {
    if ((navigator.userAgent.indexOf('Opera') >= 0) || (navigator.userAgent.indexOf('OPR') >= 0)) {
      return 'Opera'
    } else if (window.navigator.userAgent.indexOf('Edg/') > -1) { // If Chromium Edge
      return 'Edge'
    } else if (
      (navigator.userAgent.indexOf('MSIE') !== -1) || (!!document.documentMode === true) ||
      (navigator.userAgent.indexOf('Edge') > -1)
    ) { // IF IE > 10 or Edge Legacy (EdgeHTML)
      return 'IE'
    } else if (navigator.userAgent.indexOf('Chrome') !== -1) {
      return 'Chrome'
    } else if (navigator.userAgent.indexOf('Safari') !== -1) {
      return 'Safari'
    } else if (navigator.userAgent.indexOf('Firefox') !== -1) {
      return 'Firefox'
    } else {
      return 'unknown'
    }
  },
  isObject (data) {
    return (typeof data === 'object' && !Array.isArray(data) && data !== null)
  },
  is1920View () {
    if (window.innerWidth >= 1920) return true
    return false
  },
  is1280View () {
    if (window.innerWidth < 1920) return true
    return false
  },
  isCtrlKeyPressed (event = {}) {
    return event.ctrlKey
  },
  formatDurationToMinutesAndSeconds (params) {
    // For audio player format
    if (params.forAudioPlayer) {
      const h = Math.floor(params.duration / 3600)
      const m = Math.floor(params.duration % 3600 / 60)
      const s = Math.floor(params.duration % 3600 % 60)

      if (h > 0) {
        return `${(h < 10) ? `0${h}` : h}:${(m < 10) ? `0${m}` : m}:${(s < 10) ? `0${s}` : s}` // HH:mm:ss
      } else {
        return `${(m < 10) ? `0${m}` : m}:${(s < 10) ? `0${s}` : s}` // mm:ss
      }
    } else {
      const durationAsSeconds = moment.duration(Math.floor(params.duration), 'seconds')

      const hours = durationAsSeconds.get('hours') < 10 ? `0${durationAsSeconds.get('hours')}` : String(durationAsSeconds.get('hours'))
      const minutes = durationAsSeconds.get('minutes') < 10 ? `0${durationAsSeconds.get('minutes')}` : String(durationAsSeconds.get('minutes'))
      let seconds = durationAsSeconds.get('seconds') < 10 ? `0${durationAsSeconds.get('seconds')}` : String(durationAsSeconds.get('seconds'))

      // Add extra point
      const durationWithDecimalPoint = (params.duration.toString().indexOf('.') >= 0) ? params.duration.toString().split('.') : null
      const fractionValue = (durationWithDecimalPoint && durationWithDecimalPoint.length >= 0) ? durationWithDecimalPoint[durationWithDecimalPoint.length - 1] : null
      if (fractionValue && fractionValue !== '00') {
        seconds = parseFloat(`${seconds}.${fractionValue}`).toFixed(0)

        if (seconds < 10) seconds = `0${seconds}` // Should add 0 if less than 9
      }

      let res = `${hours}:${minutes}:${seconds}`

      // Default value
      if (isNaN(params.duration) || params.duration === 'NaN') {
        res = '00:00:10'
      }

      return res
    }
  },

  /**
   * @deprecated use `rgbToHex` or `hexToRgb` instead
   */
  handleColorCodeConversion (colorVal, convertFrom, convertTo, alphaSrc) {
    if (convertFrom === 'rgba' && convertTo === 'hex') {
      let rgb = colorVal
      rgb = rgb.match(/^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*(\d*(?:\.\d+)?)\)$/)

      // Regular 6 digits hex value
      if (!rgb || rgb.length < 4) return colorVal

      let hexVal = ('0' + parseInt(rgb[1], 10).toString(16)).slice(-2) +
        ('0' + parseInt(rgb[2], 10).toString(16)).slice(-2) +
        ('0' + parseInt(rgb[3], 10).toString(16)).slice(-2)

      // Calculate alhpa
      if (rgb.length === 5) {
        const alpha = Math.round(parseFloat(rgb[4]) * 255)
        const alphaHex = ('0' + alpha.toString(16)).slice(-2)
        hexVal = alphaHex + hexVal
      }

      return `#${hexVal}`.toUpperCase()
    }

    if (convertFrom === 'hex' && convertTo === 'rgba') {
      const hexVal = colorVal
      let alpha = alphaSrc
      let red = 0
      let green = 0
      let blue = 0

      if (alphaSrc === undefined) {
        alpha = 1
      }

      if (hexVal.length === 9) {
        alpha = hexVal.substr(1, 2)
        alpha = (parseInt(alpha, 16)) / 255
        red = parseInt(hexVal.substr(3, 2), 16)
        green = parseInt(hexVal.substr(5, 2), 16)
        blue = parseInt(hexVal.substr(7, 2), 16)
      } else if (hexVal.length === 7) {
        red = parseInt(hexVal.substr(1, 2), 16)
        green = parseInt(hexVal.substr(3, 2), 16)
        blue = parseInt(hexVal.substr(5, 2), 16)
      }

      return `rgba(${red}, ${green}, ${blue}, ${alpha})`
    }

    return colorVal
  },
  infiniteScroll (containerId, callback) {
    const handleScroll = () => {
      const scrollHeight = document.getElementById(containerId).scrollHeight
      const scrollTop = document.getElementById(containerId).scrollTop
      const offsetHeight = document.getElementById(containerId).offsetHeight
      const calcScrollbar = scrollHeight - scrollTop - offsetHeight + 1

      // Scrollbar is in the bottom part then run the callback
      if ((calcScrollbar < 2) && (typeof (callback) === 'function')) {
        callback()
      }
    }

    if (document.getElementById(containerId)) {
      document.getElementById(containerId).addEventListener('scroll', handleScroll)
    }
  },
  /**
    Get Timezones from moment and filter each names
  */
  getTimeZones () {
    const timeZone = []
    for (const i in MomentTimezone.tz.names()) {
      const tz = MomentTimezone.tz.names()[i]
      timeZone.push({
        value: tz,
        label: tz.replace('/', ' ')
      })
    }
    return timeZone
  },
  getCurrentTime () {
    return moment().format('HH:mm:ss')
  },
  filterMediaName (mediaName) {
    const maxChar = 200
    if (mediaName.length > maxChar) {
      return `${mediaName.substring(0, 27)}...`
    } else {
      return mediaName
    }
  },
  /**
   *
   * @param {String} previewUrl
   * @returns String url
   */
  filterMediaPreviewUrl (previewUrlSrc, params = {}) {
    const previewUrl = new URL(previewUrlSrc)
    if (params.queryParams) {
      for (const name of Object.keys(params.queryParams)) {
        if (params.queryParams[name]) {
          previewUrl.searchParams.set(name, params.queryParams[name])
        }
      }
    }

    return previewUrl.href
  },
  parseLedTag (tag = '') {
    const [value, width, height, name] = tag.match(/LED:(\d+)x(\d+)[x]*(.*)/)

    return {
      width: parseInt(width, 10),
      height: parseInt(height, 10),
      name: name === '' ? `${width}x${height}` : name,
      dimension: `${width}x${height}`,
      queryStringValue: value.replace('LED:', ''),
      value
    }
  },
  filterTagOptionsForUI (params) {
    const $currentUser = Dependencies.inject('$currentUser')
    const tags = this.clone(params.tags)
    let newTags = []
    const tagItemColorOptions = ['#52f9e5', '#4bccdb', '#628bc8', '#7471c5', '#9443c2']

    tags.forEach((tag, indx) => {
      let tagItem = {
        value: null,
        backgroundColor: '',
        configTag: false
      }

      if (typeof (tag) === 'string') {
        tagItem.value = tag
      } else {
        tagItem = tag

        if (tag.name && !tagItem.value) {
          tagItem.value = tag.name
        }
      }

      // Adjust color
      if (tagItem.color) {
        tagItem.backgroundColor = tagItem.color
      } else {
        tagItem.backgroundColor = `${tagItemColorOptions[indx % tagItemColorOptions.length]}`
      }

      // Hide tags if not included in current user tag rights
      if ($currentUser.getUserTags().length && !$currentUser.getUserTags().includes(tagItem.value)) {
        tagItem.hideByTagUserRights = true
      }

      // Show label
      if (params.showLabel) {
        tagItem.showLabel = tagItem.value

        if (params.removeLEDPrefixForLabel && tagItem.value.includes('LED:')) {
          tagItem.showLabel = this.parseLedTag(tagItem.value).name
        }

        if (params.videowall && tagItem.value.includes('LED:')) {
          tagItem.showLabel = this.parseLedTag(tagItem.value).name
          tagItem.videowall = true
        }
      }

      // Make sure to have name
      if (!tagItem.name && tagItem.value) {
        tagItem.name = tagItem.value
      }

      // Actions
      if (params.disabledDeleteAction) tagItem.disabledDeleteActionabel = true
      if (params.disabledEditAction) tagItem.disabledEditAction = true

      // Update to normal color
      if (params.setNormalColors) tagItem.backgroundColor = `${tagItemColorOptions[indx % tagItemColorOptions.length]}`

      // As config tag
      if (tagItem.value.trim().split(':').length > 1) tagItem.configTag = true

      if (params.ledDimensionTag) tagItem.ledDimensionTag = true

      newTags.push(tagItem)
    })

    newTags = this.sortTags({ tags: newTags })

    return newTags
  },
  formatPageTagWithColors (tag, tagsSummary, storeOnLoadTagsFromSlider) {
    let formattedTag = tag

    if (typeof (tag) === 'string') {
      formattedTag = { value: tag }
    }

    // Get color from tags slider
    const detailsFromSlider = storeOnLoadTagsFromSlider.find((t) => { return t.name === formattedTag.value })
    if (detailsFromSlider && detailsFromSlider.color) {
      formattedTag.color = this.handleColorCodeConversion(detailsFromSlider.color, 'hex', 'rgba', 0.6)
    }

    if (!detailsFromSlider) {
      const tDetails = tagsSummary.find((t) => { return t.name === formattedTag.value })

      if (tDetails && tDetails.color) {
        formattedTag.color = this.handleColorCodeConversion(tDetails.color, 'hex', 'rgba', 0.6)
      }
    }

    return formattedTag
  },
  filterProjectDataForBackend (params) {
    if (!params || (params && !params.project)) return

    const project = this.clone(params.project)
    let targetData = (project.data && project.data.pages) ? project.data.pages : []

    if (project.data && project.data.audio) {
      targetData = project.data.audio
    }

    targetData.forEach((data, indx) => {
      const item = targetData[indx]

      // Do not send preview with url to the backend
      if (item.preview) {
        delete item.preview
      }

      if (item.media && item.media.preview) {
        delete item.media.preview
      }

      if (this.isObject(item)) {
        for (const itmKey in item) {
          if (item[itmKey] && item[itmKey].preview) {
            delete item[itmKey].preview
          }

          // Remove previousFontWeight to reset value each time project is saved, this is used for UI purposes and should not be stored in the backend
          if (item[itmKey] && item[itmKey].previousFontWeight) {
            delete item[itmKey].previousFontWeight
          }
        }
      }

      delete item._edit
    })

    return project
  },
  convertDurationToSeconds (durationVal) {
    durationVal = '' + durationVal
    const durationWithColon = (durationVal.indexOf(':') >= 0) ? durationVal.split(':') : null
    const durationWithDot = (durationVal.indexOf('.') >= 0) ? durationVal.split('.') : null
    let durationInSeconds = parseFloat(durationVal).toFixed(2)

    if (durationWithColon) {
      if (durationWithColon.length === 3) { // hh:mm:seconds
        durationInSeconds = (+durationWithColon[0]) * 60 * 60 + (+durationWithColon[1]) * 60 + (+durationWithColon[2])
      } else if (durationWithColon.length === 2) { // mm:ss
        durationInSeconds = (+durationWithColon[0] * 60) + (+durationWithColon[1])
      } else if (durationWithColon.length === 1) { // mm:ss
        durationInSeconds = durationWithColon[0]
      }
    } else if (durationWithDot) {
      if (durationWithDot.length === 3) { // hh:mm:seconds
        durationInSeconds = (+durationWithDot[0]) * 60 * 60 + (+durationWithDot[1]) * 60 + (+durationWithDot[2])
      } else if (durationWithDot.length === 2) { // mm:ss
        durationInSeconds = durationVal
      } else if (durationWithDot.length === 1) { // mm:ss
        durationInSeconds = durationWithDot[0]
      }
    }

    return durationInSeconds
  },
  getPageNamesWithGreyBackgroundColor () {
    return [
      'media', 'create-template-set', 'create-project-template', 'edit-project-template', 'edit-project-audio', 'led-configuration', 'videowall', 'schedules', 'player', 'virtual-player-iframe',
      'specific-schedule', 'user-admin', 'generalTermsAndConditionsOfSale', 'alert', 'tos', 'distributor', 'activation', 'reporting', 'monitoring', 'smartText',
      'cookieList', 'privacy', 'schedule-v2'
    ]
  },
  getBackgroundColor (params) {
    if (typeof (params) === 'undefined' || params === null) params = { indx: 0 }

    let colorOptions = ['#52f9e5', '#4bccdb', '#628bc8', '#7471c5', '#9443c2']

    if (params.colorOptions) colorOptions = params.colorOptions

    return `${colorOptions[params.indx % colorOptions.length]}`
  },
  checkIfInWhitePageZone (valYPosition) {
    const topOffsetOfWhitePageEl = $('.page.white').offset() !== undefined ? $('.page.white').offset().top : null
    if (topOffsetOfWhitePageEl === null) return 'whitePageNotFound'
    if (valYPosition >= topOffsetOfWhitePageEl) return 'inside'
    return 'outside'
  },
  // TODO: utils should probably not have api calls
  downloadDocument (params) {
    const $api = this.getApiInstance()
    const dataType = (params.dataType) ? params.dataType : 'application/pdf'
    const label = params.label
    let reqParams = {}

    if (params.reqParams) reqParams = params.reqParams // Set request params
    if (params.showLoader) this.mainLoader(true) // start loader

    $api.get(params.url, {
      responseType: 'blob',
      params: reqParams
    }).then(({
      data
    }) => {
      const blob = new Blob([data], { type: dataType })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = label
      link.click()
      URL.revokeObjectURL(link.href)
      this.mainLoader(false)
    }).catch((error) => {
      if (_.isFunction(params.onError)) {
        params.onError(error)
      } else {
        console.log(error)
      }
    })
  },
  isPageForLicenses (pageName) {
    let res = false

    // Validate by page name
    if (pageName) {
      res = ['distributor', 'activation'].indexOf(pageName) >= 0
    }

    // Validate by Hostname
    if (!res && window.location.hostname === CONFIG.FRONTEND_ACTIVATE_DOMAIN) {
      res = true
    }

    return res
  },
  isPageForEnterprise (pageName) {
    let res = false
    // Validate by page name
    if (pageName) {
      res = [].indexOf(pageName) >= 0
    }

    // Validate by Hostname
    if (window.location.hostname.startsWith('arya-enterprise')) {
      res = true
    }

    return res
  },
  /**
   * Check if the page is disabled for self sign up from
   * config using SELF_SIGN_UP_DISALLOWED.
   *
   * @returns {Boolean} true if self sign up is disabled, false otherwise
   */
  isSignUpDisallowed () {
    return !!CONFIG.SELF_SIGN_UP_DISALLOWED
  },
  /**
   * Check if the hub feature flag is enabled.
   * @returns {Boolean} true if hub feature flag is enabled, false otherwise
   */
  hasHub () {
    return CONFIG.HAS_HUB
  },
  /**
   * Conserve aspect ratio of the original region. Useful when shrinking/enlarging
   * images/project to fit into a certain area */
  calculateAspectRatioFit (params) {
    const srcWidth = params.srcWidth // width of source images/project
    const srcHeight = params.srcHeight // height of source images/project
    const maxWidth = params.maxWidth // maximum available width in the UI
    const maxHeight = params.maxHeight // maximum available height in the UI

    const gcd = (a, b) => {
      return (b === 0) ? a : gcd(b, a % b)
    }
    const r = gcd(srcWidth, srcHeight)
    const aspectRatio = `${(srcWidth / r)}/${(srcHeight / r)}`
    let res = {}

    if (params.ledPage) {
      /* AR >= 5/1: use 1 item per row
        AR <5/1 and AR >= 2/1: use 2 item per row
        AR <2/1 and AR >=4/3: use 3 items per row
        AR <4/3 and AR >=3/4: use 4 items per row
        AR < 3/4 and AR >=1/2: use 5 items per row
        AR < 1/2 use 6 items per row */
      let itemPerRow = 1
      const currentAR = srcWidth / srcHeight

      if (currentAR >= 5 / 1) {
        itemPerRow = 1
      } else if (currentAR < 5 / 1 && currentAR >= 2 / 1) {
        itemPerRow = 2
      } else if (currentAR < 2 / 1 && currentAR >= 4 / 3) {
        itemPerRow = 3
      } else if (currentAR < 4 / 3 && currentAR >= 3 / 4) {
        itemPerRow = 4
      } else if (currentAR < 3 / 4 && currentAR >= 1 / 2) {
        itemPerRow = 5
      } else if (currentAR < 1 / 2) {
        itemPerRow = 6
      }

      // Adjust card dimension based on content
      const scrollBarWidth = 20
      const previewMargin = 14
      const totalMargins = (previewMargin * itemPerRow) + scrollBarWidth // Add all margins on each item per row and space for scrollbar

      const newMaxWidth = (params.contentElWidth - totalMargins) / itemPerRow
      let newMaxHeight = newMaxWidth

      // Adjust Max Height for vertical to avoid greater than 6 cards per row
      if (parseInt(srcHeight) > parseInt(srcWidth)) {
        newMaxHeight = 20000
      }

      const newRatio = Math.min(newMaxWidth / srcWidth, newMaxHeight / srcHeight)
      const cardWidth = srcWidth * newRatio
      const cardHeight = srcHeight * newRatio

      res = {
        cardWidth,
        cardHeight,
        previewWidth: cardWidth,
        previewHeight: cardHeight,
        nameWidth: cardWidth - previewMargin,
        itemPerRow,
        aspectRatio
      }
    } else {
      const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight)
      const cardWidth = Math.trunc(srcWidth * ratio)
      const cardHeight = Math.trunc(srcHeight * ratio)

      res = {
        cardWidth,
        cardHeight,
        previewWidth: cardWidth,
        previewHeight: cardHeight,
        nameWidth: cardWidth,
        itemPerRow: 1,
        aspectRatio
      }
    }

    return res
  },
  calculateSortOrderValue (params) {
    if (!params) params = {}

    const topSiblingOrderVal = params?.topSiblingOrder ?? false
    const bottomSiblingOrderVal = params?.bottomSiblingOrder ?? false
    const previousOrderVal = params?.previousOrderVal ?? ''
    const isAtTheFirstPosition = !topSiblingOrderVal
    const isAtTheMiddlePosition = topSiblingOrderVal && bottomSiblingOrderVal
    const isAtTheLastPosition = topSiblingOrderVal && !bottomSiblingOrderVal
    const generateNewOrder = !topSiblingOrderVal && !bottomSiblingOrderVal
    const forceToTop = params.forceToTop

    const createNewOrderVal = (p) => {
      let newOrder = new Date().getTime()
      const mediaIndexVal = (p.mediaIndex) ? Math.floor(p.mediaIndex) + 1 : 0
      const scheduleIndexVal = (p.scheduleIndex) ? Math.floor(p.scheduleIndex) + 1 : 0
      const playtimeIndexVal = (p.playtimeIndex) ? Math.floor(p.playtimeIndex) + 1 * 10 : 0

      const uniqueRef = Math.floor(`${mediaIndexVal}${scheduleIndexVal}${playtimeIndexVal}${Math.floor(Math.random() * 10)}`)
      newOrder += uniqueRef

      return newOrder.toString(16)
    }

    /**
     * Force to top. Use the bottom order value and subtract by (less than Jan 1 2018)
     */
    if (forceToTop) {
      const getDate = new Date(1514764800000) // Jan 01, 2018
      const topSiblingOrderValHexa = getDate.getTime() - Math.floor(Math.random() * 100)
      const previousFirstPositionMediaOrderHexa = parseInt(previousOrderVal, 16)
      const getRandNumberBtwnTheTwoF = Math.floor(topSiblingOrderValHexa + Math.random() * (previousFirstPositionMediaOrderHexa - topSiblingOrderValHexa))
      return getRandNumberBtwnTheTwoF.toString(16)
    }

    // Generate new order value
    if (generateNewOrder) {
      return createNewOrderVal(params)
    }

    // First position
    if (isAtTheFirstPosition) {
      const getDate = new Date(1514764800000) // Jan 01, 2018
      const topSiblingOrderValHexa = getDate.getTime()

      // Template sort in template sets
      if (params.templateInSets) {
        return moment(bottomSiblingOrderVal).utc().subtract(1, 'month').endOf('day').valueOf()
      }

      // Common
      const previousFirstPositionMediaOrderHexa = parseInt(bottomSiblingOrderVal, 16)
      const getRandNumberBtwnTheTwoF = Math.floor(topSiblingOrderValHexa + Math.random() * (previousFirstPositionMediaOrderHexa - topSiblingOrderValHexa))

      return getRandNumberBtwnTheTwoF.toString(16)
    }

    // Middle position
    if (isAtTheMiddlePosition) {
      // Template sort in template sets
      if (params.templateInSets) {
        return Math.floor(Math.random() * (bottomSiblingOrderVal - topSiblingOrderVal) + topSiblingOrderVal)
      }

      // Common
      const topSiblingOrderValHexa = parseInt(topSiblingOrderVal, 16)
      const bottomSiblingOrderValHexa = parseInt(bottomSiblingOrderVal, 16)
      const getRandNumberBtwnTheTwo = Math.floor(topSiblingOrderValHexa + Math.random() * (bottomSiblingOrderValHexa - topSiblingOrderValHexa))

      return getRandNumberBtwnTheTwo.toString(16)
    }

    // Last Position
    if (isAtTheLastPosition) {
      // Template sort in template sets
      if (params.templateInSets) {
        return moment(topSiblingOrderVal).utc().add(1, 'month').endOf('day').valueOf()
      }

      return createNewOrderVal(params)
    }

    return null
  },
  updatePageComponentStyle (params) {
    if (!params) return

    const getTotalHeightAllTopSlider = () => {
      let totalHeight = 0
      $('.top-slider-filter').each((indx, el) => {
        totalHeight += $(el).height()
      })
      return totalHeight
    }

    const isTagSliderDragActive = $('#tagSlider').length ? $('#tagSlider').hasClass('drag-active') : false
    const pageWhiteHeight = $('.page.white').height()
    const topOptionsEl = $('.cpl-main').find('.options').length ? $('.cpl-main').find('.options') : null
    const topOptionsHeight = topOptionsEl ? topOptionsEl[0].clientHeight : 0
    const tagSliderHeight = $('#tagSlider').length ? $('#tagSlider').height() : 0
    const popIdentifierHeight = $('#popIdentifierSlider').length ? $('#popIdentifierSlider').height() : 0
    const isMediaPickerVisible = () => {
      return ($('#mediaList').length && $('#mediaList').hasClass('visible'))
    }

    const adjustPage = {
      commonMainContent: (params = {}) => {
        // Main content row default style
        const mainContentStyle = {
          marginTop: `${getTotalHeightAllTopSlider()}px`,
          height: `${pageWhiteHeight - (topOptionsHeight + getTotalHeightAllTopSlider())}px`
        }

        // PopId slider default style
        const popIdentifierStyle = {
          top: `${topOptionsHeight + tagSliderHeight}px`
        }

        // LedTagSlider default style
        const ledTagSliderStyle = {
          top: `${topOptionsHeight + tagSliderHeight + popIdentifierHeight}px`
        }

        // Create or Edit Project Audio page
        if (params.projectAudio) {
          let audioHeaderHeight = 110

          if (this.is1280View()) {
            audioHeaderHeight = 60
          }

          mainContentStyle.marginTop = `${audioHeaderHeight}px`
          mainContentStyle.height = `${pageWhiteHeight - (topOptionsHeight + audioHeaderHeight)}px`
        }

        // Schedule page
        if (params.schedulePage) {
          if ($('.main-content-row').hasClass('has-item-selected')) {
            let getMarginTop = getTotalHeightAllTopSlider()

            // Add margin from schedule tags slider top margin
            if (getMarginTop) {
              getMarginTop += 11
            }

            mainContentStyle.marginTop = `${getMarginTop}px`
            mainContentStyle.height = `${(pageWhiteHeight - topOptionsHeight) - getMarginTop}px`
          }
        }

        // User DB page
        if (params.userDbPageDefault) {
          const smartTextTagsSliderEl = $('#smartTextTagsSlider').length ? $('#smartTextTagsSlider') : false
          const smarTextTagSliderHeight = smartTextTagsSliderEl ? smartTextTagsSliderEl.height() : 0
          mainContentStyle.marginTop = `${smarTextTagSliderHeight}px`
          mainContentStyle.height = `${pageWhiteHeight - (topOptionsHeight + smarTextTagSliderHeight + 26)}px`
        }

        if (params.adminPage) {
          // Need to account for additional top margin for admin page sub header (See AdminPageHeader.vue) which is mt-8 (32px)
          mainContentStyle.height = `${pageWhiteHeight - (topOptionsHeight + getTotalHeightAllTopSlider()) - 32}px`
        }

        // Set Page main-content-row style
        if ($('.main-content-row').length && !isTagSliderDragActive) {
          $('.main-content-row').css(mainContentStyle)
        }

        // Set PoP Identifier slider style
        const popIdentifierSliderEl = $('#popIdentifierSlider')
        if (popIdentifierSliderEl.length) {
          popIdentifierSliderEl.css(popIdentifierStyle)
        }

        // Set Led tags slider style
        const ledTagSliderEl = $('#ledTagSlider')
        if (ledTagSliderEl.length) {
          ledTagSliderEl.css(ledTagSliderStyle)
        }
      },
      adminPage: () => {
        adjustPage.commonMainContent({
          adminPage: true
        })
      },
      createEditProjectPage: () => {
        if (isMediaPickerVisible()) return

        adjustPage.commonMainContent({
          createEditProjectPage: true
        })
      },
      createEditProjectAudioPage: (params) => {
        adjustPage.commonMainContent({
          projectAudio: true
        })

        // Adjust Project Page Menu
        const styleVal = {
          marginTop: '27px',
          height: 'height: calc(100% - 96px)'
        }
        const topOptionsEl = $('.cpl-main').find('.options').length ? $('.cpl-main').find('.options') : false
        const pageHeight = $('.page.white').height()

        if (topOptionsEl) {
          let marginTopOffset = 12

          if (this.is1280View()) marginTopOffset = 28

          styleVal.marginTop = `${(topOptionsEl.height()) - ($('.template-action-options').height() + marginTopOffset)}px`
          styleVal.height = pageHeight - (topOptionsEl.height())
        }

        $('#editProjectAudioPlaylistSection').css(styleVal)
      },
      mediaDropzone: () => {
        const rSideOffsetVal = 20
        const styleVal = {}
        styleVal.top = `${tagSliderHeight + rSideOffsetVal}px`

        const targetEl = $('#mediaFileUpload')
        if (targetEl.length && !isTagSliderDragActive) targetEl.css(styleVal)
      },
      mediaFiles: () => {
        adjustPage.commonMainContent()
        adjustPage.mediaDropzone()
      },
      playerPage: () => {
        adjustPage.commonMainContent({ playerPage: true })
      },
      schedulePage: () => {
        adjustPage.commonMainContent({ schedulePage: true })
      },
      schedulePagePlayers: () => {
        adjustPage.commonMainContent({ schedulePage: true })
      },
      ledPage: (params) => {
        adjustPage.commonMainContent({ ledPage: true })
      },
      mediaListInModal: () => {
        const mediaListModalEl = $('#mediaList')
        const mediaListItemContainerEl = $('#mediaListItemContainer')
        const tagSliderEl = mediaListModalEl.find('#tagSlider').length ? $('#tagSlider') : null
        const tagSliderElHeight = tagSliderEl ? tagSliderEl.height() : 0
        const popIdsSliderEl = mediaListModalEl.find('#popIdentifierSlider').length ? $('#popIdentifierSlider') : null
        const popIdsSliderElHeight = popIdsSliderEl ? popIdsSliderEl.height() : 0
        const mediaListItemContainerOffset = 60
        const bottomActionsHeight = mediaListModalEl.find('.media-selection-action').length ? mediaListModalEl.find('.media-selection-action').height() : 0

        // Update PopId slider style
        if (popIdsSliderEl) {
          const popIdsSliderTop = tagSliderElHeight + mediaListItemContainerOffset
          popIdsSliderEl.css({
            top: `${popIdsSliderTop}px`,
            opacity: 1
          })
        }

        // Update Media list modal style
        if (params.animateMediaListModalContent) {
          mediaListItemContainerEl.addClass('animate-height-changed')
        }

        mediaListItemContainerEl.css({
          height: `${mediaListModalEl.height() - (tagSliderElHeight + popIdsSliderElHeight + bottomActionsHeight + mediaListItemContainerOffset)}px`,
          bottom: bottomActionsHeight ? `${bottomActionsHeight}px` : 0
        })
      },
      reportingPage: (params) => {
        adjustPage.commonMainContent({
          reportingPage: true
        })
      },
      monitoringPage: () => {
        adjustPage.commonMainContent({
          monitoringPage: true
        })
      },
      userDbPageDefault: () => {
        adjustPage.commonMainContent({
          userDbPageDefault: true
        })
      }
    }

    if (params.name && adjustPage[params.name]) {
      if (['mediaAlert', 'covid-19'].indexOf(params.name) >= 0) {
        adjustPage.mediaFiles()
      } else {
        adjustPage[params.name](params)
      }
    }
  },
  handleCustomTooltip ({ targetEl, showTooltip, fullText, addTop = 5 }) {
    const parentEl = $(targetEl)
    const customTagItemTooltipEl = $('#customTagItemTooltip').length ? $('#customTagItemTooltip') : null
    const styleVal = {
      top: `${parentEl.offset().top + parentEl.height() + addTop}px`,
      left: `${parentEl.offset().left}px`
    }

    if (showTooltip) {
      if (!customTagItemTooltipEl) {
        $('#app').append($(`<div style="left: ${styleVal.left}; top: ${styleVal.top}" id="customTagItemTooltip" class="custom-tag-item-tooltip">${fullText}</div>`))
      } else {
        customTagItemTooltipEl.text(fullText)
        customTagItemTooltipEl.css(styleVal)
        customTagItemTooltipEl.removeClass('hide')
      }
    } else {
      if (customTagItemTooltipEl) {
        customTagItemTooltipEl.addClass('hide')
      }
    }
  },
  detectEscapeKeyPressed (callback) {
    $(document.onkeydown = (e) => {
      if (['Escape', 'Esc'].includes(e.key)) {
        if (_.isFunction(callback)) {
          callback()
        }
      }
    })
  },
  encodeQueryString (obj = {}) {
    const queryString = []

    for (const key in obj) {
      const value = obj[key]
      const encodeKey = encodeURIComponent(key)

      if (Array.isArray(value)) {
        // Encode each element of the array individually.
        queryString.push(...value.map((element) => `${encodeKey}=${encodeURIComponent(element)}`))
      } else if (typeof value === 'object') {
        // Recursively encode nested objects.
        queryString.push(`${encodeKey}=${this.encodeQueryString(value)}`)
      } else {
        // Encode the value directly.
        queryString.push(`${encodeKey}=${encodeURIComponent(value)}`)
      }
    }

    return queryString.join('&')
  },
  decodeQueryString (queryString = '') {
    const obj = {}
    const params = queryString.split('&')

    for (const param of params) {
      // this is not canonical pair key=value
      if (param.indexOf('=') < 0) {
        continue
      }

      // Split on the FIRST `=` only — values can contain literal `=`
      // (e.g. base64-encoded pagination tokens such as `nextToken=eyJH...==`).
      // Destructuring `param.split('=')` would silently drop everything after
      // the second `=`, corrupting those values.
      const eqIdx = param.indexOf('=')
      const key = param.slice(0, eqIdx)
      const value = param.slice(eqIdx + 1)
      // `+` is the URL-encoded form of a space (application/x-www-form-urlencoded).
      // decodeURIComponent alone doesn't handle it, so replace before decoding.
      const decodedValue = decodeURIComponent(value.replace(/\+/g, '%20'))
      let decodedKey = decodeURIComponent(key.replace(/\+/g, '%20'))

      if (decodedKey.startsWith('?')) {
        decodedKey = decodedKey.substr(1)
      }

      if (decodedKey in obj) {
        // If the key already exists, add the value to the array.
        if (Array.isArray(obj[decodedKey])) {
          obj[decodedKey].push(decodedValue)
        } else {
          obj[decodedKey] = [obj[decodedKey], decodedValue]
        }
      } else {
        // If the key does not exist, create a new array to store the values.
        obj[decodedKey] = [decodedValue]
      }
    }

    return obj
  },
  getURL () {
    return new URL(window.location.href)
  },
  updateWindowLocationQueryParams (params = []) {
    // Do not proceed if params is not an array or app is empty because it can cause errors in unit testing
    if (!_.isArray(params)) return

    const url = this.getURL()
    const oldPath = `${url.pathname}${url.search}`
    let newPath = url.pathname

    // Create query params as object with keys
    const residuals = this.decodeQueryString(url.search)
    let queryParams = {}
    for (const q of params) {
      // array in params.values and there's no state in the URL
      if (_.isArray(q.values)) {
        queryParams[q.key] = q.values
      }
    }

    // preserve everything which is irrelevant to params
    for (const [key, value] of Object.entries(residuals)) {
      if (!queryParams[key]) {
        queryParams[key] = value
      }
    }

    const queryString = this.encodeQueryString(queryParams)
    if (!_.isEmpty(queryString)) {
      newPath = `${newPath}?${queryString}`
    }

    queryParams = _.pickBy(queryParams, (q) => !_.isEmpty(q))

    // injectint routerInstance when necessary
    const router = Dependencies.inject('$routerInstance')

    if (newPath !== oldPath && router && router.push) {
      if (_.isEmpty(_.flatMapDeep(queryParams))) { queryParams = {} }
      router.push({ path: newPath, query: queryParams, replace: true })
    }
  },
  getWindowLocationQueryParams () {
    const url = this.getURL()
    return this.decodeQueryString(url.search)
  },
  /**
   * Proxy an event to the parent element. Example usage this.$utils.proxyEvent(this, 'close', 'closeWizard', data)
   * @param {*} params object with two fields {src, dst} name of source and destination events respectively
   * @param {*} sourceArguments original arguments passed
   */
  proxyEvent (component, sourceEvent, destinationEvent, sourceArguments) {
    component.$emit(destinationEvent, {
      sourceArguments,
      sourceEventName: sourceEvent
    })
  },

  serialize (source) {
    return JSON.stringify(source)
  },

  /**
   * Converts rgb or rgba to hex
   * @param {string} rgbValue ex: 'rgb(0, 0, 0)' or 'rgba(0, 0, 0, 1)'
   * @param {boolean} isARGBFormat true if hex should use #AARRGGBB instead of #RRGGBBAA
   * @returns {string | null} hex value as a string
   */
  rgbToHex (rgbValue, isARGBFormat) {
    if (!rgbValue || typeof rgbValue !== 'string') {
      return null
    }

    // Check if the input is already in hex format
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(rgbValue)) {
      return rgbValue
    }

    // Check if the input is not a valid rgb(a) format
    const rgbRegex = /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/
    const rgbaRegex = /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0?\.\d+|1|0)\s*\)$/
    if (!rgbRegex.test(rgbValue) && !rgbaRegex.test(rgbValue)) {
      return null
    }

    const rgba = rgbValue.match(/[\d.]+/g).map(Number)

    // Check if alpha is incorrectly supplied in rgb
    if (rgbRegex.test(rgbValue) && rgba.length === 4) {
      return null
    }

    // Check if rgba is used but no alpha value found
    if (rgbaRegex.test(rgbValue) && rgba.length !== 4) {
      return null
    }

    const [r, g, b, a] = rgbValue
      .replace(/\s+/g, '')
      .match(/(rgba?)|(\d+(\.\d+)?%?)|(\.\d+)/g)
      .slice(1) ?? []

    const hex = (color) => Math.max(0, Math.min(255, parseInt(color ?? 0, 10))).toString(16).padStart(2, '0')
    const opacity = (fraction) => Math.min(255, Math.round(parseFloat(fraction ?? 1) * 255)).toString(16).padStart(2, '0')

    // If isARGBFormat move the alpha to the front (#AARRGGBB)
    if (isARGBFormat && a) {
      return `#${opacity(a)}${hex(r)}${hex(g)}${hex(b)}`
    }

    return `#${hex(r)}${hex(g)}${hex(b)}${a ? opacity(a) : ''}`
  },

  /**
   * Converts hex to rgb or rgba
   * @param {string} hexValue ex: '#000' or '#ffffff' or '#FFFFFF80
   * @param {boolean} isARGBFormat true if hex should use #AARRGGBB instead of #RRGGBBAA
   * @returns {string | null} hex value as a string
   */
  hexToRgb (hexValue, isARGBFormat) {
    if (!hexValue || typeof hexValue !== 'string') {
      return null
    }

    // Check if the input is already in rgb(a) format
    if (/^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(,\s*\d*\.?\d+\s*)?\)$/.test(hexValue)) {
      return hexValue
    }

    // Check if the input is not a valid hex format
    if (!/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(hexValue)) {
      return null
    }

    let newValue = hexValue.replace(/^#/, '')

    // Handle shorthand hex (#RGB or #RGBA)
    if (newValue.length === 3 || newValue.length === 4) {
      newValue = newValue.split('').map(char => char + char).join('')
    }

    let r, g, b, a

    // If isARGBFormat use the (#AARRGGBB) format
    if (isARGBFormat && newValue.length === 8) {
      a = parseInt(newValue.slice(0, 2), 16) / 255
      r = parseInt(newValue.slice(2, 4), 16)
      g = parseInt(newValue.slice(4, 6), 16)
      b = parseInt(newValue.slice(6, 8), 16)
    } else {
      r = parseInt(newValue.slice(0, 2), 16)
      g = parseInt(newValue.slice(2, 4), 16)
      b = parseInt(newValue.slice(4, 6), 16)
      a = newValue.length === 8 ? parseInt(newValue.slice(6, 8), 16) / 255 : null
    }

    // Ensure alpha is rounded to 2 decimal places
    if (a !== null) {
      a = Math.round(a * 100) / 100
    }

    return a === null
      ? `rgb(${r}, ${g}, ${b})`
      : `rgba(${r}, ${g}, ${b}, ${a})`
  },

  /**
   * Converts AWS Cognito user pool stages defined in `CONFIG` into a more readable and structured format.
   *
   * The transformation removes the `GLOBAL_STAGE` prefix (e.g., "prod-eu" → "eu") and builds an object
   * keyed by the region code. For the global stage itself, it defaults the code to `"eu"`.
   *
   * @returns {Object.<string, { code: string, stage: string, config: object, translationKey: string }>}
   * An object where each key is a region code and the value is an object containing:
   * - `code`: the simplified region code (e.g., "eu")
   * - `stage`: the original key from the Cognito config (e.g., "prod-eu")
   * - `config`: the original configuration object from the user pool config
   * - `translationKey`: a string used for localizing the region name (e.g., "regionNames.eu")
   */
  getFormattedStages () {
    const globalStage = CONFIG.GLOBAL_STAGE;
    const stages = CONFIG.AWS_COGNITO_USER_POOLS;
    const newStages = {};

    for (const key of Object.keys(stages)) {
      const code = key !== globalStage
        ? key.match(new RegExp(`^${globalStage}-(.*)$`))?.[1]
        : 'eu';

      if (!code) continue;

      newStages[code] = {
        code,
        stage: key,
        config: stages[key],
        translationKey: `regionNames.${code}`,
      };
    }

    return newStages;
  },

  /**
   * Return the API URL for the stage provided.
   * The stage is determined from the user's session. It
   * defaults to the global stage defined in CONFIG.
   */
  getStageAPIUrl (stage) {
    return CONFIG.API_BASE_URLS[stage] ?? CONFIG.API_BASE_URLS[CONFIG.GLOBAL_STAGE];
  },
  /**
   * Determines the starting day of the calendar week for a given language code.
   *
   * Uses the `Intl.Locale` API to retrieve the first day of the week for the specified locale.
   * Returns 1 if the week starts on Monday, or 0 if it starts on Sunday (default/fallback).
   * This format is compatible with libraries like dayjs.
   *
   * @param {string} languageCode - The BCP 47 language tag (e.g., 'en', 'fr', 'de') to determine the week start day.
   * @returns {number} Returns 1 if the week starts on Monday, 0 if it starts on Sunday.
   */
  getCalendarWeekStart (languageCode) {
    let firstDayOfWeek = 0; // Default to Sunday
    try {
      const loc = new Intl.Locale(languageCode || 'en');
      if (typeof loc.getWeekInfo === 'function') {
        // getWeekInfo.firstDay returns 1 for Monday, 7 for Sunday
        firstDayOfWeek = loc.getWeekInfo().firstDay;
      } else {
        // Fallback for Firefox since it does not support getWeekInfo
        firstDayOfWeek = 0
      }
    } catch (error) {
      firstDayOfWeek = 0; // Fallback to Sunday if the locale is invalid
    }

    // Return 1 for Monday, 0 for Sunday which is what is expected by dayjs
    return firstDayOfWeek === 1 ? 1 : 0;
  },

  /**
   * Get the URL of a specific product from the configuration.
   * Optionally appends a locale query string if specified and applicable.
   * @param {*} product Product name (e.g., 'hub', 'arya', 'activate', 'assets', 'integrations', 'hub-settings', 'hub-profile')
   * @param {*} appendLocaleQuery Locale code to append as query string (e.g., 'en', 'fr') or null/undefined to not append
   * @returns {string | null} URL of the product or null if not found
   */
  getProductUrlsFromConfig (product, appendLocaleQuery) {
    const productUrls = {
      arya: CONFIG.FRONTEND_DOMAIN,
      activate: CONFIG.FRONTEND_ACTIVATE_DOMAIN,
      hub: CONFIG.FRONTEND_HUB_DOMAIN,
      assets: CONFIG.FRONTEND_ASSETS_DOMAIN,
      integrations: CONFIG.FRONTEND_INTEGRATIONS_DOMAIN,
      'hub-settings': `${CONFIG.FRONTEND_HUB_DOMAIN}/settings`,
      'hub-profile': `${CONFIG.FRONTEND_HUB_DOMAIN}/profile`
    };

    const productUrl = productUrls[product] ?? null;
    let url = productUrl ? `https://${productUrl}` : null;

    // Append locale query string when redirecting to different product
    // but not to the same product or ARYA and Activate
    const selectedProduct = ['hub-settings', 'hub-profile'].includes(product) ? APP.HUB : product;
    if (
      appendLocaleQuery &&
      ![APP.ARYA, APP.ACTIVATE].includes(selectedProduct) &&
      url
    ) {
      url = `${url}?locale=${appendLocaleQuery}`;
    }

    return url;
  }
}
